// /cobros — Requiere atención (antes "Cobros pendientes"): cada suscripcion que tendria que haberse
// cobrado y no se cobro, con el motivo, lo que va a pasar si nadie hace nada
// y el boton para cobrarla ya.
//
// Nacio del incidente de 2026-08/09 (22 suscripciones un mes sin cobrarse,
// despues 20 dadas de baja por un error de configuracion) en el que no habia
// un solo lugar del backoffice donde verlas juntas. Estado sano de la pagina:
// vacia, con un mensaje que lo dice.
//
// Quinta situacion, "Cobro duplicado — a reintegrar": lo contrario, un pago
// aprobado que no se acredito porque la suscripcion ya estaba paga. Viene en
// una lista aparte (`duplicates`) y se dibuja con su propia fila, SIN boton de
// cobro: lo que hay que hacer es devolver plata, no cobrar. El reintento en
// lote recorre solo `visible`, que nunca incluye duplicados.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import {
  fetchPendingCharges,
  retrySubscriptionCharge,
  AdminApiError,
  type DuplicateCharge,
  type AppliedDuplicateCharge,
  type UnmatchedProviderPayment,
  type PendingCharge,
} from '@/lib/api';
import { money, relativeTime, absoluteTime, shortDate } from '@/lib/format';
import {
  CATEGORY_HINT,
  CATEGORY_LABEL,
  chargeSituation,
  declineLabel,
  type ChargeListSituation,
} from '@/lib/charge-state';
import { lineStatusLabel, shortPaymentId } from '@/lib/reconcile';
import { useSession } from '@/hooks/useSession';
import { canWrite } from '@/lib/access';
import { CopyButton, Pill } from '@/components/DataCard';
import ConfirmDialog from '@/components/ConfirmDialog';
import MarkRefundedButton from '@/components/MarkRefunded';
import RefundAppliedDuplicateButton from '@/components/RefundAppliedDuplicate';
import ResolveUnmatchedPaymentButton from '@/components/ResolveUnmatchedPayment';
import ProviderLookupId from '@/components/ProviderLookupId';
import ChargeRetryButton, {
  RetryExplanation,
  RetryNotice,
  describeRetry,
  type RetryNoticeKind,
} from '@/components/ChargeRetry';
import { fieldCls } from '@/lib/form-styles';

// El duplicado va primero: es plata que ya se le cobró de más a alguien.
const ORDER: ChargeListSituation[] = ['duplicate', 'blocked', 'overdue', 'grace', 'expired'];

const TONE: Record<ChargeListSituation, 'error' | 'warning' | 'neutral'> = {
  duplicate: 'error',
  blocked: 'error',
  overdue: 'error',
  grace: 'warning',
  expired: 'neutral',
};

const CARD_BORDER: Record<ChargeListSituation, string> = {
  duplicate: 'border-status-error/50',
  blocked: 'border-status-error/50',
  overdue: 'border-status-error/50',
  grace: 'border-status-warning/50',
  expired: 'border-surface-border dark:border-surface-dark-border',
};

export default function PendingCharges(): JSX.Element {
  const session = useSession();
  const canRetry = canWrite(session, 'users');
  const qc = useQueryClient();
  const [filter, setFilter] = useState<ChargeListSituation | 'all'>('all');
  const [partner, setPartner] = useState<string>('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState<{ done: number; total: number; running: boolean } | null>(null);
  const [rowNotice, setRowNotice] = useState<Record<string, { kind: RetryNoticeKind; text: string }>>(
    {},
  );

  const q = useQuery({
    queryKey: ['admin-pending-charges'],
    queryFn: () => fetchPendingCharges(),
    refetchInterval: 60_000,
  });

  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const duplicates = useMemo(() => q.data?.duplicates ?? [], [q.data]);
  const applied = useMemo(() => q.data?.applied_duplicates ?? [], [q.data]);
  const unmatched = useMemo(() => q.data?.unmatched_payments ?? [], [q.data]);
  const partners = useMemo(
    () =>
      [
        ...new Set([
          ...items,
          ...duplicates,
          ...applied,
          ...unmatched,
        ].map((i) => i.partner_id)),
      ].sort(),
    [items, duplicates, applied, unmatched],
  );
  // Suscripciones a cobrar que se ven (y que cobra el reintento en lote).
  const visible = items.filter(
    (i) => (filter === 'all' || i.category === filter) && (!partner || i.partner_id === partner),
  );
  const partnerDuplicates = duplicates.filter((d) => !partner || d.partner_id === partner);
  const partnerApplied = applied.filter((d) => !partner || d.partner_id === partner);
  const partnerUnmatched = unmatched.filter((d) => !partner || d.partner_id === partner);
  const visibleDuplicates = filter === 'all' || filter === 'duplicate' ? partnerDuplicates : [];
  const visibleApplied = filter === 'all' || filter === 'duplicate' ? partnerApplied : [];
  // No hay chip de situación "pagos no identificados": son otra tarjeta. Con
  // un filtro activo no corresponde mostrarlos (2026-10-23).
  const visibleUnmatched = filter === 'all' ? partnerUnmatched : [];
  const totals = ORDER.reduce(
    (acc, c) => ({
      ...acc,
      [c]:
        c === 'duplicate'
          ? partnerDuplicates.length + partnerApplied.length
          : items.filter((i) => i.category === c && (!partner || i.partner_id === partner)).length,
    }),
    {} as Record<ChargeListSituation, number> & { unmatched: number },
  );
  totals.unmatched = partnerUnmatched.length;

  async function runBulk(): Promise<void> {
    setBulkOpen(false);
    const targets = visible;
    setBulk({ done: 0, total: targets.length, running: true });
    // De a uno: son cobros reales contra el mismo procesador, y el operador
    // tiene que poder ver que paso con cada uno.
    for (const [idx, it] of targets.entries()) {
      try {
        const r = await retrySubscriptionCharge(it.subscription_id, 'Reintento en lote desde Requiere atención');
        setRowNotice((prev) => ({ ...prev, [it.subscription_id]: describeRetry(r) }));
      } catch (err) {
        setRowNotice((prev) => ({
          ...prev,
          [it.subscription_id]: {
            kind: 'error',
            text: err instanceof AdminApiError ? err.message : 'Error desconocido',
          },
        }));
      }
      setBulk({ done: idx + 1, total: targets.length, running: idx + 1 < targets.length });
    }
    qc.invalidateQueries({ queryKey: ['admin-pending-charges'] });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 data-help-page="charges.page" data-help="charges.page" className="text-2xl font-semibold">
            Requiere atención
          </h1>
          <p className="text-sm text-ink-muted">
            Suscripciones que tendrían que haberse cobrado y no se cobraron. Se actualiza cada minuto.
          </p>
        </div>
        <button
          type="button"
          onClick={() => q.refetch()}
          className="min-h-[2.25rem] inline-flex items-center gap-2 rounded-lg px-3 text-sm bg-ink-muted/10 hover:bg-ink-muted/20 focus-visible:ring-2 focus-visible:ring-brand-light"
        >
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} aria-hidden="true" />
          Actualizar
        </button>
      </header>

      <section data-help="charges.summary" aria-label="Resumen por situación" className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        {ORDER.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={filter === c}
            onClick={() => setFilter(filter === c ? 'all' : c)}
            className={`text-left rounded-xl border p-4 bg-surface dark:bg-surface-dark hover:bg-ink-muted/5 focus-visible:ring-2 focus-visible:ring-brand-light ${
              totals[c] > 0 ? CARD_BORDER[c] : 'border-surface-border dark:border-surface-dark-border'
            } ${filter === c ? 'ring-2 ring-brand' : ''}`}
          >
            <p className="text-xs text-ink-muted">{CATEGORY_LABEL[c]}</p>
            <p
              className={`text-3xl font-semibold tabular-nums ${
                totals[c] > 0 && TONE[c] === 'error'
                  ? 'text-status-error'
                  : totals[c] > 0 && TONE[c] === 'warning'
                    ? 'text-status-warning'
                    : ''
              }`}
            >
              {totals[c]}
            </p>
            <p className="text-xs text-ink-muted mt-1">{CATEGORY_HINT[c]}</p>
          </button>
        ))}
      </section>

      <div className="flex flex-wrap items-center gap-3">
        {partners.length > 1 ? (
          <label className="text-sm flex items-center gap-2">
            Partner
            <select
              value={partner}
              onChange={(e) => setPartner(e.target.value)}
              className={fieldCls({ block: false, extra: 'w-48' })}
            >
              <option value="">Todos</option>
              {partners.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {filter !== 'all' ? (
          <button type="button" onClick={() => setFilter('all')} className="text-sm text-brand hover:underline">
            Ver todas las situaciones
          </button>
        ) : null}
        {canRetry && visible.length > 1 ? (
          <button
            type="button"
            data-help="charges.bulk"
            onClick={() => setBulkOpen(true)}
            disabled={bulk?.running}
            className="ml-auto min-h-[2.25rem] inline-flex items-center gap-2 rounded-lg px-3 text-sm font-medium bg-brand/15 text-brand hover:bg-brand/25 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-light"
          >
            Reintentar los {visible.length} de la lista
          </button>
        ) : null}
      </div>

      {bulk ? (
        <RetryNotice
          kind={bulk.running ? 'info' : 'ok'}
          text={
            bulk.running
              ? `Cobrando ${bulk.done + 1} de ${bulk.total}…`
              : `Listo: se intentaron ${bulk.total}. El resultado de cada uno está en su fila.`
          }
          {...(bulk.running ? {} : { onClose: () => setBulk(null) })}
        />
      ) : null}

      {q.isError ? (
        <RetryNotice kind="error" text={q.error instanceof AdminApiError ? q.error.message : 'No se pudo cargar la lista.'} />
      ) : null}

      {visibleDuplicates.length > 0 ? (
        <section data-help="charges.duplicates" aria-labelledby="duplicates-title" className="space-y-3">
          <div className="rounded-xl border border-status-error/40 bg-status-error/10 px-4 py-3 text-sm text-status-error flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h2 id="duplicates-title" className="font-semibold">
                {visibleDuplicates.length === 1
                  ? 'Un cobro duplicado a reintegrar'
                  : `${visibleDuplicates.length} cobros duplicados a reintegrar`}
              </h2>
              <p className="text-xs">
                La pasarela aprobó estos pagos cuando la suscripción ya estaba paga con otro: se le
                cobró de más al cliente y el pago no se acreditó. No se cobra nada desde acá: hay
                que pedir el reintegro de cada pago con su ID en la pasarela y su monto, y después
                marcarlo como reintegrado.
              </p>
            </div>
          </div>
          <ul className="space-y-3">
            {visibleDuplicates.map((d) => (
              <DuplicateRow key={`${d.payment_provider}:${d.payment_id}`} item={d} />
            ))}
          </ul>
        </section>
      ) : null}

      {visibleApplied.length > 0 ? (
        <section data-help="charges.applied-duplicates" aria-labelledby="applied-duplicates-title" className="space-y-3">
          <div className="rounded-xl border border-status-error/40 bg-status-error/10 px-4 py-3 text-sm text-status-error flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h2 id="applied-duplicates-title" className="font-semibold">
                {visibleApplied.length === 1
                  ? 'Un cobro aplicado dos veces para el mismo período'
                  : `${visibleApplied.length} cobros aplicados dos veces para el mismo período`}
              </h2>
              <p className="text-xs">
                Los dos pagos figuran cobrados y cubren el mismo tiempo: uno sobra. Para los pares
                con período identificado, «Registrar reintegro» devuelve el ingreso, deja el plan
                intacto y emite la Nota de Crédito. Los de antes de la 0053 (sin período) quedan
                marcados «revisar».
              </p>
            </div>
          </div>
          <ul className="space-y-3">
            {visibleApplied.map((d) => (
              <AppliedDuplicateRow key={d.payment_id} item={d} />
            ))}
          </ul>
        </section>
      ) : null}

      {visibleUnmatched.length > 0 ? (
        <section data-help="charges.unmatched" aria-labelledby="unmatched-title" className="space-y-3">
          <div className="rounded-xl border border-status-error/40 bg-status-error/10 px-4 py-3 text-sm text-status-error flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h2 id="unmatched-title" className="font-semibold">
                {visibleUnmatched.length === 1
                  ? 'Un pago no se pudo atribuir a una cuenta'
                  : `${visibleUnmatched.length} pagos no se pudieron atribuir a una cuenta`}
              </h2>
              <p className="text-xs">
                La pasarela informó estos pagos y la referencia no existe, es de otra cuenta o
                faltó. Es plata cobrada sin dueño conocido: hay que identificar la cuenta (o
                descartarlo) y resolverlo para que deje de sonar.
              </p>
            </div>
          </div>
          <ul className="space-y-3">
            {visibleUnmatched.map((d) => (
              <UnmatchedRow key={d.id} item={d} />
            ))}
          </ul>
        </section>
      ) : null}

      {q.isSuccess && visible.length === 0 && visibleDuplicates.length === 0 && visibleApplied.length === 0 && visibleUnmatched.length === 0 ? (
        <div className="rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark p-8 text-center space-y-2">
          <CheckCircle2 size={28} className="mx-auto text-status-success" aria-hidden="true" />
          <p className="font-medium">
            {items.length === 0 &&
            duplicates.length === 0 &&
            applied.length === 0 &&
            unmatched.length === 0
              ? 'Nada requiere atención.'
              : 'Nada en esta situación.'}
          </p>
          <p className="text-sm text-ink-muted">
            Todas las suscripciones con renovación automática se están cobrando en fecha.
          </p>
        </div>
      ) : null}

      {visible.length > 0 ? (
        <ul data-help="charges.list" className="space-y-3">
          {visible.map((it) => (
            <PendingRow
              key={it.subscription_id}
              item={it}
              canRetry={canRetry}
              notice={rowNotice[it.subscription_id] ?? null}
            />
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={bulkOpen}
        title={`Reintentar ${visible.length} cobros ahora`}
        description={
          <div className="space-y-3">
            <p>
              Se va a intentar cobrar, de a uno, a las {visible.length} suscripciones de la lista
              {filter !== 'all' ? ` (${CATEGORY_LABEL[filter].toLowerCase()})` : ''}
              {partner ? ` de ${partner}` : ''}.
            </p>
            <RetryExplanation expired={visible.some((v) => v.status === 'expired')} />
            <p className="text-xs">
              Si el procesador sigue fallando, van a fallar todos igual: corregí la causa antes.
            </p>
          </div>
        }
        confirmLabel="Cobrar todos"
        onConfirm={() => void runBulk()}
        onCancel={() => setBulkOpen(false)}
      />
    </div>
  );
}

function PendingRow({
  item,
  canRetry,
  notice,
}: {
  item: PendingCharge;
  canRetry: boolean;
  notice: { kind: RetryNoticeKind; text: string } | null;
}): JSX.Element {
  const s = chargeSituation({ ...item, has_token: true, auto_renew: true });
  const border =
    s?.tone === 'error'
      ? 'border-l-status-error'
      : s?.tone === 'warning'
        ? 'border-l-status-warning'
        : 'border-l-ink-muted/40';
  return (
    <li
      className={`rounded-xl border border-surface-border dark:border-surface-dark-border border-l-4 ${border} bg-surface dark:bg-surface-dark p-4 flex flex-col md:flex-row md:items-start gap-4`}
    >
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link
            to={`/users/${item.user_id}`}
            className="font-medium text-brand hover:underline truncate"
          >
            {item.display_name || 'Usuario sin nombre'}
          </Link>
          {item.display_name ? null : (
            <span className="font-mono text-xs text-ink-subtle">{item.user_id.slice(0, 8)}</span>
          )}
          <Pill tone="neutral">{item.partner_id}</Pill>
          {item.plan_name ? <span className="text-sm text-ink-muted">{item.plan_name}</span> : null}
          {item.price_amount ? (
            <span className="text-sm font-medium tabular-nums">
              {money(item.price_amount, item.price_currency ?? 'ARS')}
            </span>
          ) : null}
        </div>
        {s ? (
          <>
            <p className={`text-sm font-medium ${s.tone === 'error' ? 'text-status-error' : s.tone === 'warning' ? 'text-status-warning' : ''}`}>
              {s.title}
            </p>
            {s.detail ? <p className="text-xs text-ink-muted break-words">{s.detail}</p> : null}
            <p className="text-xs text-ink-muted">{s.next}</p>
          </>
        ) : null}
        <p className="text-xs text-ink-subtle" title={absoluteTime(item.current_period_end)}>
          Vencimiento del período: {relativeTime(item.current_period_end)}
        </p>
      </div>
      <div className="md:w-72 shrink-0 space-y-2">
        <ChargeRetryButton
          size="sm"
          disabled={!canRetry}
          disabledReason="Requiere Usuarios en nivel completo"
          target={{
            subscriptionId: item.subscription_id,
            status: item.status,
            customer: item.display_name,
            planName: item.plan_name,
            amount: item.price_amount,
            currency: item.price_currency,
          }}
        />
        {notice ? <RetryNotice {...notice} /> : null}
      </div>
    </li>
  );
}

/**
 * Un pago cobrado de más. Sin botón de cobro: lo que hay que hacer es pedir el
 * reintegro de ESTE pago (ID + monto). El nombre lleva a la ficha, donde está
 * el intento acreditado que sostiene el plan.
 */
function DuplicateRow({ item }: { item: DuplicateCharge }): JSX.Element {
  const reason = declineLabel(item.reason);
  const line = lineStatusLabel(item.provider_status);
  return (
    <li className="rounded-xl border border-surface-border dark:border-surface-dark-border border-l-4 border-l-status-error bg-surface dark:bg-surface-dark p-4 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Link to={`/users/${item.user_id}`} className="font-medium text-brand hover:underline truncate">
          {item.display_name || 'Usuario sin nombre'}
        </Link>
        {item.display_name ? null : (
          <span className="font-mono text-xs text-ink-subtle">{item.user_id.slice(0, 8)}</span>
        )}
        <Pill tone="neutral">{item.partner_id}</Pill>
        {item.plan_name ? <span className="text-sm text-ink-muted">{item.plan_name}</span> : null}
        <span className="text-sm font-semibold tabular-nums text-status-error">
          {money(item.amount, item.currency)}
        </span>
      </div>
      <p className="text-sm font-medium text-status-error">{CATEGORY_LABEL.duplicate}</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
        <span className="inline-flex items-center gap-0.5 font-mono" title={item.payment_id}>
          ID de pago: {shortPaymentId(item.payment_id)}
          <CopyButton value={item.payment_id} label="ID de pago" />
        </span>
        <ProviderLookupId id={item.provider_lookup_id} provider={item.payment_provider} showMissing />
        <span title={absoluteTime(item.created_at)}>
          Pagado el {shortDate(item.created_at)} · {relativeTime(item.created_at)}
        </span>
        {item.detected_at ? (
          <span title={absoluteTime(item.detected_at)}>Detectado {relativeTime(item.detected_at)}</span>
        ) : null}
      </div>
      {reason || line ? (
        <p className="text-xs text-ink-muted break-words">
          {[reason ? `Motivo: ${reason}` : null, line ? `Line: ${line}` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-ink-muted">
          Pedí el reintegro de este pago, no del acreditado: el acreditado es el que sostiene el
          plan. Cuando esté hecho, marcalo.
        </p>
        <MarkRefundedButton
          userId={item.user_id}
          paymentId={item.payment_id}
          amount={item.amount}
          currency={item.currency}
          customer={item.display_name}
        />
      </div>
    </li>
  );
}

/** Cobro duplicado que SI se aplico (dos `succeeded` del mismo periodo). */
function AppliedDuplicateRow({ item }: { item: AppliedDuplicateCharge }): JSX.Element {
  const samePeriod = item.kind === 'same_period';
  return (
    <li className="rounded-xl border border-surface-border dark:border-surface-dark-border border-l-4 border-l-status-error bg-surface dark:bg-surface-dark p-4 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Link to={`/users/${item.user_id}`} className="font-medium text-brand hover:underline truncate">
          {item.display_name || 'Usuario sin nombre'}
        </Link>
        {item.display_name ? null : (
          <span className="font-mono text-xs text-ink-subtle">{item.user_id.slice(0, 8)}</span>
        )}
        <Pill tone="neutral">{item.partner_id}</Pill>
        {item.plan_name ? <span className="text-sm text-ink-muted">{item.plan_name}</span> : null}
        <span className="text-sm font-semibold tabular-nums text-status-error">
          {money(item.amount, item.currency)}
        </span>
      </div>
      <p className="text-sm font-medium text-status-error">Cobro duplicado aplicado — a reintegrar</p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
        <span className="inline-flex items-center gap-0.5 font-mono" title={item.external_payment_id ?? undefined}>
          ID de pago: {shortPaymentId(item.external_payment_id ?? item.payment_id)}
          {item.external_payment_id ? (
            <CopyButton value={item.external_payment_id} label="ID de pago" />
          ) : null}
        </span>
        <ProviderLookupId id={item.provider_lookup_id} provider="line" showMissing />
        <span title={absoluteTime(item.created_at)}>
          Pagado el {shortDate(item.created_at)} · {relativeTime(item.created_at)}
        </span>
      </div>
      <p className="text-xs text-ink-muted">
        {item.period_start && item.period_end
          ? `Período cobrado: ${shortDate(item.period_start)} → ${shortDate(item.period_end)}. `
          : 'Pago previo al registro de períodos. '}
        El otro cobro ({shortPaymentId(item.other_external_payment_id ?? item.other_payment_id)}) es
        el que sostiene el plan.
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-ink-muted">
          {samePeriod
            ? 'Pedí el reintegro de ESTE pago en la pasarela y después registralo acá.'
            : 'Pago previo al registro de períodos: confirmá los dos cobros con la pasarela, pedí el reintegro de ESTE y después registralo acá.'}
        </p>
        {/* Misma regla y mismo boton que la ficha del cliente (duplicate-pairs.ts):
            el backend solo acepta reintegrar el cobro que sobra del par. */}
        <RefundAppliedDuplicateButton
          userId={item.user_id}
          paymentId={item.external_payment_id ?? item.payment_id}
          amount={item.amount}
          currency={item.currency}
          customer={item.display_name}
          pairedPaymentId={item.other_external_payment_id ?? item.other_payment_id}
        />
      </div>
    </li>
  );
}

/** Pago informado por la pasarela que no se pudo atribuir a una cuenta. */
function UnmatchedRow({ item }: { item: UnmatchedProviderPayment }): JSX.Element {
  const reason =
    item.reason === 'missing_reference'
      ? 'El webhook no traía referencia'
      : item.reason === 'invalid_reference'
        ? 'La referencia no tiene el formato de una suscripción'
        : item.reason === 'partner_mismatch'
        ? 'La referencia es de otra cuenta'
        : 'La referencia no existe en nuestra base';
  return (
    <li className="rounded-xl border border-surface-border dark:border-surface-dark-border border-l-4 border-l-status-error bg-surface dark:bg-surface-dark p-4 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Pill tone="neutral">{item.partner_id}</Pill>
        <Pill tone="neutral">{item.provider}</Pill>
        {item.amount ? (
          <span className="text-sm font-semibold tabular-nums text-status-error">
            {money(item.amount, item.currency ?? 'ARS')}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
        <span className="inline-flex items-center gap-0.5 font-mono" title={item.external_payment_id}>
          ID de pago: {shortPaymentId(item.external_payment_id)}
          <CopyButton value={item.external_payment_id} label="ID de pago" />
        </span>
        {item.reference ? (
          <span className="font-mono break-all">Ref: {item.reference}</span>
        ) : null}
        <span title={absoluteTime(item.received_at)}>
          Recibido el {shortDate(item.received_at)} · {relativeTime(item.received_at)}
        </span>
      </div>
      <p className="text-xs text-ink-muted">{reason}.</p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-ink-muted">
          Si el pago se acreditó por otro camino, resolvelo con el motivo; si falta acreditarlo,
          hacelo primero desde la ficha del cliente.
        </p>
        <ResolveUnmatchedPaymentButton id={item.id} paymentId={item.external_payment_id} />
      </div>
    </li>
  );
}
